'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type Currency = 'USD' | 'EUR' | 'GBP'

interface CurrencyContextType {
  currency: Currency
  setCurrency: (currency: Currency) => void
  formatPrice: (price: number) => string
  getCurrencySymbol: () => string
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined)

const exchangeRates = {
  USD: 1.0,
  EUR: 0.85,
  GBP: 0.73,
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency>('USD')

  // Load currency from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('currency') as Currency
    if (savedCurrency && ['USD', 'EUR', 'GBP'].includes(savedCurrency)) {
      setCurrency(savedCurrency)
    }
  }, [])

  // Save currency to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('currency', currency)
  }, [currency])

  const formatPrice = (price: number) => {
    const convertedPrice = price * exchangeRates[currency]
    const symbol = getCurrencySymbol()
    return `${symbol}${convertedPrice.toFixed(2)}`
  }

  const getCurrencySymbol = () => {
    switch (currency) {
      case 'USD':
        return '$'
      case 'EUR':
        return '€'
      case 'GBP':
        return '£'
      default:
        return '$'
    }
  }

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        formatPrice,
        getCurrencySymbol,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  )
}

export function useCurrency() {
  const context = useContext(CurrencyContext)
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}
